import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Newspaper } from "lucide-react";
import { listUpdates } from "../lib/api";
import {
    chipClasses, dotClasses, formatDate, getCategory,
} from "../lib/updates";

/**
 * Horizontal strip of the 3 newest updates posts — rendered on the
 * homepage to keep the news flow visible without a dedicated nav click.
 */
export default function LatestUpdatesStrip() {
    const [posts, setPosts] = useState([]);

    useEffect(() => {
        let cancelled = false;
        listUpdates({ limit: 3 })
            .then((data) => { if (!cancelled) setPosts(data); })
            .catch(() => { /* silent — strip just won't render */ });
        return () => { cancelled = true; };
    }, []);

    if (posts.length === 0) return null;

    return (
        <section
            id="updates-strip"
            className="py-20 md:py-24 bg-white border-t border-slate-100"
            data-testid="updates-strip-section"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 border border-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
                            <Newspaper className="h-3 w-3" /> Updates
                        </div>
                        <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                            Seneste fra Cleany.
                        </h2>
                        <p className="mt-2 text-base text-slate-600 max-w-xl leading-relaxed">
                            Produktnyheder, branche-tips og guides — direkte fra teamet.
                        </p>
                    </div>
                    <Link
                        to="/updates"
                        data-testid="updates-strip-see-all"
                        className="group inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 hover:text-sky-900 transition-colors"
                    >
                        Se alle indlæg
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                </div>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {posts.map((p, i) => {
                        const c = getCategory(p.category);
                        return (
                            <motion.div
                                key={p.slug}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: i * 0.08 }}
                            >
                                <Link
                                    to={`/updates/${p.slug}`}
                                    data-testid={`updates-strip-card-${p.slug}`}
                                    className="group block h-full rounded-2xl bg-white border border-slate-200 overflow-hidden hover:border-sky-300 hover:-translate-y-1 hover:shadow-lg transition-all"
                                >
                                    <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                                        {p.cover && (
                                            <img
                                                src={p.cover}
                                                alt={p.title}
                                                loading="lazy"
                                                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                                            />
                                        )}
                                    </div>
                                    <div className="p-5">
                                        <div className="flex items-center gap-2 text-[11px] text-slate-500">
                                            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${chipClasses(c.color)}`}>
                                                <span className={`h-1.5 w-1.5 rounded-full ${dotClasses(c.color)}`} />
                                                {c.label}
                                            </span>
                                            <span>{formatDate(p.date)}</span>
                                        </div>
                                        <h3 className="mt-3 font-display text-lg font-bold text-slate-900 group-hover:text-sky-700 transition-colors line-clamp-2 leading-snug">
                                            {p.title}
                                        </h3>
                                        <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">
                                            {p.excerpt}
                                        </p>
                                    </div>
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
